import type { Currency } from '@seaber/common/graphql/types';
import type { computeTceForTimeData } from '../shipment/TCE';
import { formatCurrency } from '../../lib/format';
import React from 'react';
import type { VoyageTooltipContent } from './types';
import { Text, Tip } from './Common.styled';
import { Box } from '../ui';

export function TceDataTooltip(props: {
  baseCurrency: Currency | null;
  tceData: NonNullable<ReturnType<typeof computeTceForTimeData>>;
}) {
  return (
    <div>
      <p>
        <strong>TCE</strong>:{' '}
        {formatCurrency(props.tceData?.tce ?? 0, 0, props.baseCurrency)} / day
        <br />
        <strong>Total income</strong>:{' '}
        {formatCurrency(props.tceData?.income ?? 0, 0, props.baseCurrency)}
        <br />
        <strong>Total cost</strong>:{' '}
        {formatCurrency(props.tceData?.totalCost ?? 0, 0, props.baseCurrency)}
        <br />
        <strong>Total bunker cost</strong>:{' '}
        {formatCurrency(
          props.tceData?.totalBunkerCost ?? 0,
          0,
          props.baseCurrency,
        )}
        <br />
        <strong>Total port cost</strong>:{' '}
        {formatCurrency(
          props.tceData?.totalPortCost ?? 0,
          0,
          props.baseCurrency,
        )}
        <br />
        <strong>Total extra costs</strong>:{' '}
        {formatCurrency(
          props.tceData?.totalExtraCosts ?? 0,
          0,
          props.baseCurrency,
        )}
        <br />
        <strong>Total channel cost</strong>:{' '}
        {formatCurrency(
          props.tceData?.totalChannelCost ?? 0,
          0,
          props.baseCurrency,
        )}
        <br />
      </p>
    </div>
  );
}

export function ProfitTooltip(props: {
  baseCurrency: Currency | null;
  tceData: NonNullable<ReturnType<typeof computeTceForTimeData>>;
}) {
  return (
    <div>
      <p>
        <strong>Voyage profit</strong>:{' '}
        {formatCurrency(
          props.tceData.income - props.tceData.totalCost,
          0,
          props.baseCurrency,
        )}
        <br />
        <strong>Total income</strong>:{' '}
        {formatCurrency(props.tceData.income ?? 0, 0, props.baseCurrency)}
        <br />
        <strong>Total cost</strong>:{' '}
        {formatCurrency(props.tceData.totalCost ?? 0, 0, props.baseCurrency)}
      </p>
    </div>
  );
}

export const VoyageTooltip = ({
  title,
  vesselName,
  voyageName,
  vesselOrders,
  vesselVolume,
  eta,
  etd,
  vesselCapacity,
  totalPlannedVolume,
  totalPlannedMass,
  assigner,
  isPortCostMissing,
  totalCost,
  vcp: vcp,
  externalShipmentId,
}: VoyageTooltipContent): JSX.Element => {
  return (
    <Tip>
      <Text
        fontWeight="bold"
        color="neutral.gray_700"
        mb={voyageName || vesselCapacity ? 1 : 0}
      >
        {title}
      </Text>
      {isPortCostMissing ? (
        <Box backgroundColor="voyageEvents.violation" padding="2px" mb="1">
          <Text fontWeight="bold" color="white">
            {`Missing or 0 port cost for a port call`}
          </Text>
        </Box>
      ) : null}
      {voyageName ? (
        <Text color="neutral.gray_430">Voyage Name: {voyageName}</Text>
      ) : null}
      {vesselName ? (
        <Text color="neutral.gray_430">Vessel Name: {vesselName}</Text>
      ) : null}
      {assigner ? (
        <Text color="neutral.gray_430">Assigner: {assigner}</Text>
      ) : null}
      {totalCost ? (
        <Text color="neutral.gray_430">Total Cost: {totalCost}</Text>
      ) : null}
      {eta ? <Text color="neutral.gray_430">ETA: {eta}</Text> : null}
      {etd ? <Text color="neutral.gray_430">ETD: {etd}</Text> : null}

      {vesselVolume ? (
        <Text color="neutral.gray_430">
          Vessel volume capacity: {vesselVolume}
        </Text>
      ) : null}
      {vesselCapacity ? (
        <Text color="neutral.gray_430">
          Vessel mass capacity: {vesselCapacity}
        </Text>
      ) : null}
      {totalPlannedVolume ? (
        <Text color="neutral.gray_430">
          Total planned volume: {totalPlannedVolume}
        </Text>
      ) : null}
      {totalPlannedMass ? (
        <Text color="neutral.gray_430">
          Total planned mass: {totalPlannedMass}
        </Text>
      ) : null}
      {vcp ? (
        <Text color="neutral.gray_430">VCP: {Math.round(vcp)}</Text>
      ) : null}
      {vesselOrders && vesselOrders.length ? (
        <>
          <Text>Included Orders:</Text>
          {vesselOrders.map((x: string, i: number) => (
            <Text key={i} color="neutral.gray_430">
              {x}
            </Text>
          ))}
        </>
      ) : null}
      {externalShipmentId ? (
        <Text color="neutral.gray_430">
          External Shipment ID: {externalShipmentId}
        </Text>
      ) : null}
    </Tip>
  );
};
